/**
 * Multi-body tether feasibility (TDD §4.3) — the novel, conservative core. Players have
 * nonzero `bodyWidth`, so the group can't overlap nor all fit an arbitrarily narrow window.
 * Co-occupancy + platform group-width are split so they don't double-report: geometry owns
 * `platform`; co-occupancy here covers non-platform simultaneous windows.
 */
import type { LevelFinding } from '../../types.js';
import type { SpatialLevel } from './schema.js';
import type { TraversalGrammar } from './grammar.js';

/** Tightest spread the group can reach given the run-up before the segment. */
export function spreadAtEntry(g: TraversalGrammar, runwayBefore: number): number {
  const timeAvailable = runwayBefore / g.autoRunSpeed;
  return Math.max(g.minGroupSpread, g.maxGroupSpread - g.closeRate * timeAvailable);
}

export function checkTether(
  level: SpatialLevel,
  g: TraversalGrammar,
  runways: number[],
): LevelFinding[] {
  const findings: LevelFinding[] = [];
  const segments = level.segments ?? [];

  segments.forEach((seg, i) => {
    const at = { vertexId: seg.toId };
    const runway = runways[i];

    // Co-occupancy: a shared window all members must occupy at once (non-platform).
    if (seg.requiresSimultaneous && seg.kind !== 'platform') {
      const w = seg.span;
      if (g.minGroupFootprint + g.clearanceMargin > w) {
        findings.push({
          severity: 'error',
          code: 'tether-group-too-wide-for-window',
          message: `window ${w}m can't hold ${g.minGroupFootprint}m of bodies + ${g.clearanceMargin}m clearance (no run-up rescues this)`,
          at,
        });
      } else if (spreadAtEntry(g, runway) > w - g.bodyWidth - g.clearanceMargin) {
        findings.push({
          severity: 'error',
          code: 'tether-cannot-bunch-in-time',
          message: `group can only bunch to spread ${spreadAtEntry(g, runway).toFixed(2)}m in ${runway.toFixed(2)}m run-up; window ${w}m needs <= ${(w - g.bodyWidth - g.clearanceMargin).toFixed(2)}m`,
          at,
        });
      }
    }

    if (seg.kind === 'reversal') {
      const clearWindowS = seg.clearWindowS ?? 0; // schema guarantees presence
      const occupied = seg.span + spreadAtEntry(g, runway) + g.bodyWidth;
      const time = occupied / Math.abs(g.backpedalSpeed);
      if (time > clearWindowS) {
        findings.push({
          severity: 'error',
          code: 'reversal-too-long-for-group',
          message: `trailing member needs ${time.toFixed(2)}s to clear ${occupied.toFixed(2)}m at backpedal speed; window is ${clearWindowS}s`,
          at,
        });
      }
    }

    if (seg.kind === 'collapse') {
      const collapseSpeed = seg.collapseSpeed ?? 0; // schema guarantees presence
      const required = collapseSpeed * (1 + g.collapseSafetyMargin);
      if (g.draggedGroupSpeed < required) {
        findings.push({
          severity: 'error',
          code: 'collapse-outruns-dragged-group',
          message: `dragged group speed ${g.draggedGroupSpeed.toFixed(2)}m/s < required ${required.toFixed(2)}m/s (collapse ${collapseSpeed} + ${g.collapseSafetyMargin * 100}% margin)`,
          at,
        });
      }
    }
  });

  return findings;
}
